import { useMutation } from "@apollo/client";
import gql from "graphql-tag";
import { useRealmApp } from "../RealmApp";

export default function useItemMutations() {
  const app = useRealmApp();
  return {
    addItem: useAddItem(app.currentUser.id),
    updateItem: useUpdateItem(app.currentUser.id),
    deleteItem: useDeleteItem(app.currentUser.id),
  };
}

const AddItemMutation = gql`
  mutation AddItem($item: ItemInsertInput!) {
    addedItem: insertOneItem(data: $item) {
      _id
      sku
      name
      invoiceName
      description
      gtin
      barcode
      price
      tax1
      tax2
      tax3
      subcategoryId
      tags
      color
      image
      stockMode
      minStock

      favorite
      pinToStart
      alwaysAskPrice
      includesCommission
      disabled
      hidden

      useScale
      variantId
      variantConfig {
        pricePerVariant
        variants {
          variant
          price
          priceListId
        }
      }
      modifierConfig {
        _id
        excludeList
      }
      mixAndMatch {
        steps {
          name
          items {
            items
            quantity
            price
          }
        }
      }
      updateDatetime
      creationDatetime
    }
  }
`;

const UpdateItemMutation = gql`
  mutation UpdateItem(
    $itemId: ObjectId!
    $ownerId: String!
    $updates: ItemUpdateInput!
  ) {
    updatedItem: updateOneItem(
      query: { _id: $itemId, owner_id: $ownerId }
      set: $updates
    ) {
      _id
      sku
      name
      invoiceName
      description
      gtin
      barcode
      price
      tax1
      tax2
      tax3
      subcategoryId
      tags
      color
      image
      stockMode
      minStock
      favorite
      pinToStart
      alwaysAskPrice
      includesCommission
      disabled
      hidden

      useScale
      variantId
      variantConfig {
        pricePerVariant
        variants {
          variant
          price
          priceListId
        }
      }

      modifierConfig {
        _id
        excludeList
      }
      mixAndMatch {
        steps {
          name
          items {
            items
            quantity
            price
          }
        }
      }
      updateDatetime
      creationDatetime
    }
  }
`;

const DeleteItemMutation = gql`
  mutation DeleteItem($itemId: ObjectId!, $ownerId: String!) {
    deletedItem: deleteOneItem(query: { _id: $itemId, owner_id: $ownerId }) {
      _id
    }
  }
`;

const ItemFieldsFragment = gql`
  fragment ItemFields on Item {
    _id
    sku
    name
    invoiceName
    description
    gtin
    barcode
    price
    tax1
    tax2
    tax3
    subcategoryId
    tags
    color
    image
    stockMode
    minStock
    favorite
    pinToStart
    alwaysAskPrice
    includesCommission
    disabled
    hidden

    useScale
    variantId
    modifierConfig {
      _id
      excludeList
    }
    mixAndMatch {
      steps {
        name
        items {
          items
          quantity
          price
        }
      }
    }
    updateDatetime
    creationDatetime
  }
`;

function useAddItem(ownerId) {
  const [addItemMutation] = useMutation(AddItemMutation, {
    // Manually save added Items into the Apollo cache so that Item queries automatically update
    // For details, refer to https://www.apollographql.com/docs/react/data/mutations/#making-all-other-cache-updates
    update: (cache, { data: { addedItem } }) => {
      cache.modify({
        fields: {
          items: (existingItems = []) => [
            ...existingItems,
            cache.writeFragment({
              data: addedItem,
              fragment: ItemFieldsFragment,
            }),
          ],
        },
      });
    },
  });
  return async (item) => {
    const toAdd = {
      owner_id: ownerId,
      ...item,
    };
    if (toAdd?.modifierConfig) toAdd.modifierConfig.owner_id = ownerId;
    if (toAdd?.mixAndMatch) {
      toAdd.mixAndMatch.owner_id = ownerId;
      toAdd.mixAndMatch.steps?.forEach((step) => {
        step.owner_id = ownerId;
        step.items?.forEach((item) => {
          item.owner_id = ownerId;
        });
      });
    }
    const {
      data: { addedItem },
    } = await addItemMutation({
      variables: {
        item: toAdd,
      },
    });
    return addedItem;
  };
}

function useUpdateItem(ownerId) {
  const [updateItemMutation] = useMutation(UpdateItemMutation);
  //
  return async (item, updates) => {
    //
    if (!updates.variantId) {
      delete updates.variantId;
      updates.variantId_unset = true;
    }

    if (!updates.variantId) delete updates.variantId;
    if (updates.modifierConfig) updates.modifierConfig.owner_id = ownerId;
    if (updates.mixAndMatch) {
      updates.mixAndMatch.owner_id = ownerId;
      updates.mixAndMatch.steps?.forEach((step) => {
        step.owner_id = ownerId;
        step.items?.forEach((item) => {
          item.owner_id = ownerId;
        });
      });
    } else {
      delete updates.mixAndMatch;
      updates.mixAndMatch_unset = true;
    }
    if (!updates.modifierConfig) {
      delete updates.modifierConfig;
      updates.modifierConfig_unset = true;
    }

    const {
      data: { updatedItem },
    } = await updateItemMutation({
      variables: { itemId: item._id, ownerId, updates },
    });
    return updatedItem;
  };
}

function useDeleteItem(ownerId) {
  const [deleteItemMutation] = useMutation(DeleteItemMutation, {
    update: (cache, { data: { deletedItem } }) => {
      cache.modify({
        fields: {
          items: (existingItems = [], { readField }) =>
            existingItems.filter(
              (ref) => deletedItem._id !== readField("_id", ref)
            ),
        },
      });
    },
  });
  return async (item) => {
    const {
      data: { deletedItem },
    } = await deleteItemMutation({
      variables: { itemId: item._id, ownerId },
    });
    return deletedItem;
  };
}
